import React, { memo, forwardRef, ReactElement } from 'react';
import { useWindowSize } from 'react-use';
import { areEqual, FixedSizeGrid } from 'react-window';
import AutoSizer from 'react-virtualized-auto-sizer';

import { NearbyStation } from 'types/buoys';
import config from '../../config';
import BuoyCard from '../BuoyCard';

export const DESKTOP_HORIZ_GUTTER_SIZE = 20;
export const MOBILE_HORIZ_GUTTER_SIZE = 8;
export const DESKTOP_VERT_GUTTER_SIZE = 20;
export const MOBILE_VERT_GUTTER_SIZE = 5;

// WARNING: If you change any of these you will need to also change the scss.
// We aren't using javascript because we want to ensure the server-side render
// is able to render the cards in the correct width (SSR doesn't have access to
// device width in JS)
const SMALL_BUOY_CARD_HEIGHT = 130;
const SMALL_BUOY_CARD_WIDTH = 203;
const LARGE_BUOY_CARD_HEIGHT = 184;
const LARGE_BUOY_CARD_WIDTH = 300;

const useGutterSizes = () => {
  const { width } = useWindowSize();

  const isDesktop = width >= config.tabletLargeWidth;

  const horizontalGutterSize = isDesktop ? DESKTOP_HORIZ_GUTTER_SIZE : MOBILE_HORIZ_GUTTER_SIZE;
  const verticalGutterSize = isDesktop ? DESKTOP_VERT_GUTTER_SIZE : MOBILE_VERT_GUTTER_SIZE;

  return { horizontalGutterSize, verticalGutterSize };
};

interface GridBuoyItemProps {
  data: Array<NearbyStation>;
  columnIndex: number;
  rowIndex: number;
  style: React.CSSProperties;
}

export const GridBuoyItem = memo(({ columnIndex, rowIndex, style, data }: GridBuoyItemProps) => {
  const index = rowIndex * 2 + columnIndex;
  const buoy = data[index];

  const { horizontalGutterSize, verticalGutterSize } = useGutterSizes();

  if (!buoy) {
    return null;
  }

  return (
    <li
      className="sl-buoy-virtual-list-item"
      style={{
        ...style,
        top: Number(style.top) + verticalGutterSize,
        left: Number(style.left) + horizontalGutterSize,
        width: Number(style.width) - horizontalGutterSize,
        height: Number(style.height) - verticalGutterSize,
      }}
    >
      <BuoyCard key={buoy.id} station={buoy} withActiveState trackLocation="Map" />
    </li>
  );
}, areEqual);

GridBuoyItem.displayName = 'GridBuoyItem';

interface GridInnerElementProps {
  style: React.CSSProperties;
  rest: {
    children: Array<ReactElement>;
  };
}
export const GridInnerElement: React.FC<GridInnerElementProps> = memo(
  forwardRef(({ style, ...rest }, ref: React.ForwardedRef<HTMLUListElement>) => {
    const { horizontalGutterSize } = useGutterSizes();
    return (
      <ul
        ref={ref}
        style={{
          ...style,
          listStyleType: 'none',
          paddingLeft: horizontalGutterSize,
        }}
        {...rest}
      />
    );
  }),
  areEqual,
);

GridInnerElement.displayName = 'GridInnerElement';

interface BuoyVirtualListProps {
  buoys: Array<NearbyStation>;
  isVerticalView: boolean;
}

const BuoyVirtualList: React.FC<BuoyVirtualListProps> = ({
  isVerticalView,
  buoys,
}: BuoyVirtualListProps) => (
  <AutoSizer className="sl-buoy-virtual-list-autosizer">
    {({ height, width }: any) => {
      const desktopColumnCount = Math.floor((width || 0) / LARGE_BUOY_CARD_WIDTH);

      const rowHeight = isVerticalView
        ? LARGE_BUOY_CARD_HEIGHT + DESKTOP_VERT_GUTTER_SIZE
        : SMALL_BUOY_CARD_HEIGHT + MOBILE_VERT_GUTTER_SIZE;
      const columnWidth = isVerticalView
        ? LARGE_BUOY_CARD_WIDTH + DESKTOP_HORIZ_GUTTER_SIZE
        : SMALL_BUOY_CARD_WIDTH + MOBILE_HORIZ_GUTTER_SIZE;

      const columnCount = isVerticalView ? desktopColumnCount : buoys.length;
      const rowCount = isVerticalView ? Math.round(buoys.length / 2) : 1;

      return (
        <FixedSizeGrid
          className="sl-buoy-virtual-list-grid"
          height={height || 0}
          width={width || 0}
          columnWidth={columnWidth}
          columnCount={columnCount}
          rowHeight={rowHeight}
          rowCount={rowCount}
          style={{ overflow: isVerticalView ? 'hidden auto' : 'auto hidden' }}
          itemData={buoys}
          innerElementType={GridInnerElement}
        >
          {GridBuoyItem}
        </FixedSizeGrid>
      );
    }}
  </AutoSizer>
);

export default memo(BuoyVirtualList);
